/**
 * 主题解析入口
 * - 单主题包：同步返回包内唯一主题（模式隔离 + 无异步成本）；
 * - 多主题包（H5 全包 / dev:h5:all）：按运行时项目信息解析，H5 支持 ?theme=xx 调试强切；
 * - 皮肤解析（阶段 5）：project-config 的 skin 字段按包指派，后续可改为后台 getInfo 下发 mobileTheme。
 */
import type { Component } from 'vue';
import type { SkinDefinition, ThemeDefinition } from './index';
import { ProjectEnum } from '@/project';
import current from '@/project/current';
import { useAppStore } from '@/store';
import { componentBindings, componentMap } from './components.gen';
import { defaultTheme, themeRegistry } from './registry.gen';
import { getSkin } from './skins';

/** H5 调试参数 ?theme=xx（仅多主题包生效） */
function getDebugTheme(): string {
  // #ifdef H5
  try {
    const match = window.location.search.match(/[?&]theme=([\w.-]+)/);
    if (match) return match[1];
  }
  catch {
    // 忽略解析异常
  }
  // #endif
  return '';
}

/** 解析当前主题 code */
export function resolveThemeCode(): string {
  const codes = Object.keys(themeRegistry);
  if (codes.length === 0) return defaultTheme;
  // 单主题包：直接使用，保证同步渲染
  if (codes.length === 1) return codes[0];
  // 多主题包：调试参数优先
  const debug = getDebugTheme();
  if (debug && themeRegistry[debug]) return debug;
  // 按运行时项目信息解析
  const appStore = useAppStore();
  const project = appStore.getProject;
  if (project === ProjectEnum.SELLER && themeRegistry.seller) return 'seller';
  if (themeRegistry.m) return 'm';
  return themeRegistry[defaultTheme] ? defaultTheme : codes[0];
}

/** 当前主题定义（主题随包固定，页面 setup 期解析一次即可） */
export function useTheme(): { code: string, theme: ThemeDefinition, skin: SkinDefinition | undefined } {
  const code = resolveThemeCode();
  const theme = themeRegistry[code] ?? themeRegistry[defaultTheme];
  return { code, theme, skin: getSkin(resolveSkinCode()) };
}

/**
 * 解析当前皮肤 code（空串 = 无皮肤，链路回退主题覆写/base）。
 * 按包指派：project-config 的 skin 字段（prepare 复制到 src/project/current.ts），
 * 命中的皮肤必须在入包清单内（skins/registry.gen.ts）才生效。
 */
export function resolveSkinCode(): string {
  // 预留：后续优先读后台下发 mobileTheme，命中注册表才生效
  // const remote = (appStore.configData as any)?.mobileTheme;
  // if (remote && getSkin(remote)) return remote;
  const assigned = (current as { skin?: string }).skin ?? '';
  return assigned && getSkin(assigned) ? assigned : '';
}

/**
 * 组件绑定解析：皮肤覆写 > 主题覆写 > base。
 * 注意：小程序/App 模板编译器不支持 <component :is> 运行时组件对象，
 * 模板消费统一经 src/themes/gen/ 静态分支壳（prepare 生成）；
 * 本函数保留供 H5 运行时场景与后续皮肤组件覆写扩展使用。
 */
export function useThemedComponent(key: string): Component | undefined {
  const { code } = useTheme();
  const skin = getSkin(resolveSkinCode());
  return skin?.componentOverrides?.[key] ?? componentMap[key]?.[code] ?? componentBindings[key];
}
